import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpContextToken,
  HttpErrorResponse,
  HttpResponse
} from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { BackendApplicationService } from '../backend/backend-application.service';


export const IS_LOGGING_ENABLED = new HttpContextToken<boolean>(() => false);
export const LOG_ONLY_ON_ERROR = new HttpContextToken<boolean>(() => false);
export const APPLICATION_ID_CONTEXT = new HttpContextToken<string>(() => '');
export const PROCESS_ID_CONTEXT = new HttpContextToken<string>(() => '');

// Archived payloads are stored as blobs and read by support, so these must never reach
// them. Matched case-insensitively at any nesting depth.
const REDACTED_KEYS = new Set([
  'birthno',
  'birthnumber',
  'foedselsnummer',
  'fodselsnummer',
  'pid',
  'ssn',
  'authorization',
  'token',
  'bearertoken',
  'accesstoken',
  'access_token',
]);

function redactedStringify(value: unknown): string {
  try {
    // A replacer redacts during the single serialisation pass, so nothing has to be cloned first.
    // Returning the marker for a matched key drops that whole subtree, which is the safe direction.
    return JSON.stringify(value, (key, node) =>
      REDACTED_KEYS.has(key.toLowerCase()) ? '[redacted]' : node);
  } catch {
    return '[unserializable]';
  }
}

@Injectable()
export class AltinnRequestInterceptor implements HttpInterceptor {

  constructor(
    private readonly backendApplicationService: BackendApplicationService,
  ) {
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      tap(
        response => {
          //Log only application creation and finalization; attachments opt in via LOG_ONLY_ON_ERROR
          if (request.context.get(IS_LOGGING_ENABLED) && !request.context.get(LOG_ONLY_ON_ERROR) && response instanceof HttpResponse) {
            this.logRequest(request, response);
          }
        },
        err => {
          if (request.context.get(IS_LOGGING_ENABLED) && err instanceof HttpErrorResponse) {
            this.logError(request, err);
          }
        },
      ),
    );
  }


  private logRequest(request: HttpRequest<unknown>, response: HttpResponse<unknown>) {
    this.archive(request, redactedStringify(response), response.status, false);
  }

  private logError(request: HttpRequest<unknown>, err: HttpErrorResponse) {
    this.archive(request, redactedStringify(err), err.status, true);
  }

  private archive(request: HttpRequest<unknown>, response: string, statusCode: number, isError: boolean) {
    const applicationId = request.context.get(APPLICATION_ID_CONTEXT);
    const processId = request.context.get(PROCESS_ID_CONTEXT);

    // Archiving is telemetry — a failed archive call must never surface to the user.
    this.backendApplicationService
      .logRequest(applicationId, processId, redactedStringify(request), response, statusCode, request.urlWithParams, request.method, isError)
      .subscribe({ error: () => { } });
  }
}
